import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, map, tap, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PhonePrefixOption } from '../shared/phone-input/phone-prefixes';

export interface FormDropdownItem {
  formId: string;
  fieldId: string;
  valueKey: string;
  valueDescription: string;
  disabled?: boolean;
}

export const DISABLED_ELEMENT_PREFIX = 'ELEMENT_DISABLED_';

export interface FormDropdownResponse {
  status: boolean;
  message: string;
  data: FormDropdownItem[];
}

@Injectable({
  providedIn: 'root'
})
export class FormDropdownService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.backofficeApiUrl}/api/v1/forms-dropdowns`;
  
  // Cache per i dropdown
  private cache = new Map<string, Observable<FormDropdownResponse>>();
  
  // Cache per le mappe di decodifica
  private trusteeCategoryMap: Map<string, string> | null = null;
  private rootTableMap: Map<string, string> | null = null;
  private supplierTypeMap: Map<string, string> | null = null;

  // Cache per i prefissi telefonici
  private phonePrefixOptions: PhonePrefixOption[] | null = null;
  private phonePrefixCodesByLengthDesc: string[] = [];

  /**
   * Recupera i valori delle dropdown per un form e campo specifici
   * @param formId ID del form (es. 'CREATE_SUPPLIER')
   * @param fieldId ID del campo (es. 'SUPPLIER_TYPE', 'PLACE_NAME')
   * @returns Observable con i dati delle dropdown
   */
  getFormDropdownValues(formId: string, fieldId: string): Observable<FormDropdownResponse> {
    const cacheKey = `${formId}/${fieldId}`;
    const apiUrl = formId === 'LEGAL_PERSON' && fieldId === 'LEGAL_PERSON'
      ? `${environment.coreApiUrl}/api/v1/forms-dropdowns`
      : this.apiUrl;

    const request$ = this.http
      .get<FormDropdownResponse>(`${apiUrl}/${formId}/${fieldId}`)
      .pipe(
        map((response) => this.applyFrontendRules(response, formId, fieldId)),
        shareReplay(1),
      );

    if (formId === 'LEGAL_PERSON' && fieldId === 'LEGAL_PERSON') {
      return request$;
    }
    
    if (!this.cache.has(cacheKey)) {
      this.cache.set(cacheKey, request$);
    }
    
    return this.cache.get(cacheKey)!;
  }

  getSupplierVatCountries(): Observable<FormDropdownItem[]> {
    return this.getFormDropdownValues('CREATE_SUPPLIER', 'VAT_COUNTRY').pipe(
      map(response => {
        if (!response.status || !response.data?.some(item => item.valueKey === 'IT')) {
          throw new Error('Configurazione paesi IVA mancante o non valida');
        }
        return response.data;
      }),
    );
  }

  /**
   * Recupera i prefissi telefonici internazionali dal dropdown PHONE_PREFIX.
   * Mappa valueKey -> code (es. '+39') e valueDescription -> country (es. 'Italia'),
   * componendo la label "code country". Il risultato viene messo in cache.
   */
  getPhonePrefixes(): Observable<PhonePrefixOption[]> {
    if (this.phonePrefixOptions) {
      return of(this.phonePrefixOptions);
    }

    return this.getFormDropdownValues('PHONE_PREFIX', 'PHONE_PREFIX').pipe(
      map((response) => {
        const options = (response.data || []).map((item) => ({
          code: item.valueKey,
          country: item.valueDescription,
          label: `${item.valueKey} ${item.valueDescription}`,
        }));
        this.phonePrefixOptions = options;
        this.phonePrefixCodesByLengthDesc = options
          .map((option) => option.code)
          .sort((a, b) => b.length - a.length);
        return options;
      }),
    );
  }

  /**
   * Elenco sincrono dei codici prefisso in cache, ordinati dal piu' lungo al piu'
   * corto (per il riconoscimento del prefisso in un numero concatenato legacy).
   * Vuoto finche' getPhonePrefixes() non e' stato completato almeno una volta.
   */
  getPhonePrefixCodesSync(): string[] {
    return this.phonePrefixCodesByLengthDesc;
  }

  private applyFrontendRules(
    response: FormDropdownResponse,
    formId: string,
    fieldId: string,
  ): FormDropdownResponse {
    const isCompanyTypeDropdown =
      formId === 'LEGAL_PERSON' && fieldId === 'LEGAL_PERSON';

    if (!isCompanyTypeDropdown) {
      return response;
    }

    return {
      ...response,
      data: (response.data || []).map((item) => ({
        ...item,
        disabled: item.valueKey.startsWith(DISABLED_ELEMENT_PREFIX),
      })),
    };
  }

  /**
   * Decodifica un codice categoria fiduciario (professionalType) nella label corrispondente
   * Usa il dropdown TRUSTEE_CATEGORY come fonte centralizzata
   * @param professionalTypeCode Codice categoria (es. 'AUTO_EXPERT')
   * @returns Observable con la label tradotta (es. 'Perito Auto')
   */
  decodeProfessionalType(professionalTypeCode: string | null | undefined): Observable<string> {
    if (!professionalTypeCode) {
      return of('');
    }
    
    // Se abbiamo già la cache, usiamola
    if (this.trusteeCategoryMap) {
      return of(this.trusteeCategoryMap.get(professionalTypeCode) || professionalTypeCode);
    }
    
    // Altrimenti carichiamo i dati e costruiamo la cache
    return this.getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY').pipe(
      tap(response => {
        if (response.data) {
          this.trusteeCategoryMap = new Map();
          response.data.forEach(item => {
            this.trusteeCategoryMap!.set(item.valueKey, item.valueDescription);
          });
        }
      }),
      map(() => this.trusteeCategoryMap?.get(professionalTypeCode) || professionalTypeCode)
    );
  }
  
  /**
   * Versione sincrona della decodifica categoria fiduciario
   * Richiede che i dati siano stati precedentemente caricati
   * @param professionalTypeCode Codice categoria (es. 'AUTO_EXPERT')
   * @returns Label tradotta o il codice originale se non trovato
   */
  decodeProfessionalTypeSync(professionalTypeCode: string | null | undefined): string {
    if (!professionalTypeCode) return '';
    if (!this.trusteeCategoryMap) return professionalTypeCode;
    return this.trusteeCategoryMap.get(professionalTypeCode) || professionalTypeCode;
  }
  
  /**
   * Pre-carica i dati TRUSTEE_CATEGORY per la decodifica sincrona
   * Chiamare all'init dei componenti che necessitano di decodifica sincrona
   */
  preloadTrusteeCategories(): Observable<void> {
    return this.getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY').pipe(
      tap(response => {
        if (response.data) {
          this.trusteeCategoryMap = new Map();
          response.data.forEach(item => {
            this.trusteeCategoryMap!.set(item.valueKey, item.valueDescription);
          });
        }
      }),
      map(() => void 0)
    );
  }

  /**
   * Decodifica un codice ROOT_TABLE nella label corrispondente
   * Usa il dropdown TEMPLATE_SECTIONS/ROOT_TABLE come fonte centralizzata
   * @param rootTableCode Codice entità (es. 'SUPPLIER', 'PROFESSIONAL', 'CONTRACT')
   * @returns Observable con la label tradotta (es. 'Fornitore', 'fiduciario', 'Contratto')
   */
  decodeRootTable(rootTableCode: string | null | undefined): Observable<string> {
    if (!rootTableCode) {
      return of('');
    }
    
    // Se abbiamo già la cache, usiamola
    if (this.rootTableMap) {
      return of(this.rootTableMap.get(rootTableCode) || rootTableCode);
    }
    
    // Altrimenti carichiamo i dati e costruiamo la cache
    return this.getFormDropdownValues('TEMPLATE_SECTIONS', 'ROOT_TABLE').pipe(
      tap(response => {
        if (response.data) {
          this.rootTableMap = new Map();
          response.data.forEach(item => {
            this.rootTableMap!.set(item.valueKey, item.valueDescription);
          });
        }
      }),
      map(() => this.rootTableMap?.get(rootTableCode) || rootTableCode)
    );
  }
  
  /**
   * Versione sincrona della decodifica ROOT_TABLE
   * Richiede che i dati siano stati precedentemente caricati
   * @param rootTableCode Codice entità (es. 'SUPPLIER', 'PROFESSIONAL', 'CONTRACT')
   * @returns Label tradotta o il codice originale se non trovato
   */
  decodeRootTableSync(rootTableCode: string | null | undefined): string {
    if (!rootTableCode) {
      console.log('[FormDropdownService] decodeRootTableSync: empty code, returning empty string');
      return '';
    }
    if (!this.rootTableMap) {
      console.warn('[FormDropdownService] decodeRootTableSync: cache not loaded! Returning original code:', rootTableCode);
      return rootTableCode;
    }
    const decoded = this.rootTableMap.get(rootTableCode) || rootTableCode;
    console.log(`[FormDropdownService] decodeRootTableSync: "${rootTableCode}" → "${decoded}"`);
    return decoded;
  }
  
  /**
   * Pre-carica i dati ROOT_TABLE per la decodifica sincrona
   * Usa TEMPLATE_SECTIONS/ROOT_TABLE come nel vecchio componente
   * Chiamare all'init dei componenti che necessitano di decodifica sincrona
   */
  preloadRootTableOptions(): Observable<void> {
    console.log('[FormDropdownService] preloadRootTableOptions: starting preload...');
    return this.getFormDropdownValues('TEMPLATE_SECTIONS', 'ROOT_TABLE').pipe(
      tap(response => {
        if (response.data) {
          this.rootTableMap = new Map();
          response.data.forEach(item => {
            this.rootTableMap!.set(item.valueKey, item.valueDescription);
          });
          console.log('[FormDropdownService] preloadRootTableOptions: loaded', this.rootTableMap.size, 'items:', Array.from(this.rootTableMap.entries()));
        } else {
          console.warn('[FormDropdownService] preloadRootTableOptions: no data in response');
        }
      }),
      map(() => void 0)
    );
  }

  /**
   * Decodifica un codice tipo societa usando CREATE_SUPPLIER/SUPPLIER_TYPE.
   */
  decodeSupplierType(code: string | null | undefined): Observable<string> {
    if (!code) {
      return of('');
    }

    if (this.supplierTypeMap) {
      return of(this.supplierTypeMap.get(code) || code);
    }

    return this.getFormDropdownValues('CREATE_SUPPLIER', 'SUPPLIER_TYPE').pipe(
      tap(response => {
        if (response.data) {
          this.supplierTypeMap = new Map();
          response.data.forEach(item => {
            this.supplierTypeMap!.set(item.valueKey, item.valueDescription);
          });
        }
      }),
      map(() => this.supplierTypeMap?.get(code) || code)
    );
  }

  /**
   * Versione sincrona della decodifica tipo societa.
   * Richiede che i dati siano stati precedentemente caricati.
   */
  decodeSupplierTypeSync(code: string | null | undefined): string {
    if (!code) return '';
    if (!this.supplierTypeMap) return code;
    return this.supplierTypeMap.get(code) || code;
  }

  /**
   * Pre-carica i tipi societa per la decodifica sincrona.
   */
  preloadSupplierTypes(): Observable<void> {
    return this.getFormDropdownValues('CREATE_SUPPLIER', 'SUPPLIER_TYPE').pipe(
      tap(response => {
        if (response.data) {
          this.supplierTypeMap = new Map();
          response.data.forEach(item => {
            this.supplierTypeMap!.set(item.valueKey, item.valueDescription);
          });
        }
      }),
      map(() => void 0)
    );
  }
}
